// Route: /tests/take/$id — overlay-shell режим прохождения теста.
//
// Роут — ТОНКИЙ адаптер: весь движок живёт в
// `components/quizzes/take/attempt-view.tsx` и переиспользуется сценой «Тест»
// воркспейса курса (декрет E31). Здесь остаётся только то, что принадлежит
// роутеру: `?demo=` / `?qIndex=` / `?from=` и переходы (назад, результаты,
// тарифы).
//
// URL state: ?demo=loading|error|time-up|grading|paywall|resume, ?qIndex=N
// MSW endpoints: POST /api/quizzes/:id/start-attempt, /attempt/save-draft, /attempt/submit

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { QuizAttemptView } from "@/components/quizzes/take/attempt-view";
import { QUIZ_TAKE_DEMOS } from "@/components/quizzes/take/quiz-demo";
import { useBackFrom } from "@/hooks/use-back-from";
import { fromSearchSchema } from "@/lib/back-from";
import { useCommerceVisible } from "@/lib/module-availability/commerce";

const searchSchema = z
	.object({
		demo: z.enum(QUIZ_TAKE_DEMOS).optional(),
		qIndex: z.coerce.number().int().min(0).optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/tests/take/$id")({
	validateSearch: tolerantSearch(searchSchema),
	component: QuizTakePage,
});

function QuizTakePage(): React.ReactElement {
	const { id } = Route.useParams();
	const search = Route.useSearch();
	const navigate = useNavigate();
	const commerceVisible = useCommerceVisible();
	const goBack = useBackFrom(search.from, () => {
		void navigate({ to: "/tests" });
	});

	return (
		<QuizAttemptView
			quizId={id}
			demo={search.demo}
			initialQuestionIndex={search.qIndex}
			onExit={goBack}
			onQuestionIndexChange={(index) =>
				void navigate({
					to: "/tests/take/$id",
					params: { id },
					search: { ...search, qIndex: index },
					replace: true,
				})
			}
			onSubmitted={({ attemptId }) =>
				void navigate({
					to: "/tests/results/$attemptId",
					params: { attemptId },
					// Прокидываем `from` дальше — чтобы из results-страницы тоже
					// можно было вернуться в материал, а не в общий список.
					search: search.from ? { from: search.from } : undefined,
				})
			}
			// Переход к тарифам — только там, где они видны (в приложении из App
			// Store экран лимита кнопки не показывает вовсе, guideline 3.1.1).
			onUpgrade={commerceVisible ? () => void navigate({ to: "/pricing" }) : undefined}
		/>
	);
}
